import { useContext, useEffect, useMemo } from 'react';
import { DatadogService, CartContext } from '@water-web/view';
import * as Sentry from '@sentry/react';

import { ClarityTracker } from '@app/analytics/integrations/ClarityTracker';
import { Analytics } from '@app/analytics';
import { useUser } from '@app/hooks';

/**
 * Component to synchronize user identification across all analytics services.
 * Should be rendered AFTER AnalyticsScripts to ensure all services are initialized.
 *
 * Syncs user ID across:
 * - Microsoft Clarity
 * - Datadog RUM
 * - Rudderstack
 * - Sentry
 *
 * If user is logged in, uses the user ID.
 * If user is not logged in, uses the Rudderstack anonymous ID.
 */
export const AnalyticsUserSync = () => {
  const cartContext = useContext(CartContext);
  const analytics = useMemo(() => Analytics.getInstance(), []);
  const currentUser = useUser();

  const customer = cartContext?.cart?.getCustomer();

  useEffect(() => {
    ClarityTracker.generateSessionUrl();

    const syncUserIds = () => {
      try {
        if (currentUser) {
          const userId = currentUser.id;
          analytics.identify(userId);

          DatadogService.setUser(userId, {
            type: 'user',
            name: customer?.getFirstName() ?? undefined,
            email: customer?.getEmail() ?? undefined,
            username:
              customer?.getFirstName() && customer?.getLastName()
                ? `${customer?.getFirstName()} ${customer?.getLastName()}`.trim()
                : undefined,
          });

          Sentry.setUser({
            id: userId,
            email: customer?.getEmail() ?? undefined,
            username:
              customer?.getFirstName() && customer?.getLastName()
                ? `${customer?.getFirstName()} ${customer?.getLastName()}`.trim()
                : undefined,
          });

          return;
        }

        // Get Rudderstack anonymous ID as fallback
        const anonymousId = window.rudderanalytics?.getAnonymousId?.();

        if (!anonymousId) {
          return;
        }

        analytics.identify(anonymousId);

        DatadogService.setUser(anonymousId, {
          type: 'anonymous',
          anonymousId,
          name: customer?.getFirstName() ?? undefined,
          email: customer?.getEmail() ?? undefined,
          username:
            customer?.getFirstName() && customer?.getLastName()
              ? `${customer?.getFirstName()} ${customer?.getLastName()}`.trim()
              : undefined,
        });

        Sentry.setUser({
          id: anonymousId,
          type: 'anonymous',
          anonymousId,
          email: customer?.getEmail() ?? undefined,
          username:
            customer?.getFirstName() && customer?.getLastName()
              ? `${customer?.getFirstName()} ${customer?.getLastName()}`.trim()
              : undefined,
        });
      } catch (error) {
        // Silently fail - analytics should not break functionality
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.error('Error syncing user ID across analytics services:', error);
        }
      }
    };

    // Wait for Rudderstack to be ready, then sync
    if (window.rudderanalytics?.ready) {
      window.rudderanalytics.ready(syncUserIds);
    } else {
      // If ready method doesn't exist, try syncing anyway
      syncUserIds();
    }
  }, [currentUser, customer, analytics]);

  return null;
};
